// Feature: MCRO001-F0036 (inherits MCRO001-F0007/F0027)
// Test: T-MCRO-036

import React, { useCallback, useRef } from 'react';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// MCRO001-F0036/T-MCRO-036: an accessible scrub timeline over the Rehearsal count.
// It never derives, buffers or advances a frame/media position on its own — it only
// ever resolves a pointer/keyboard interaction to one target count and calls
// `onSeek(count)`, which is the same count-setting path Previous/Next already use.
// Count remains the sole clock authority (MCRO001-D006 §3); MCRO001-F0027's
// projection continues to derive the video frame from the authoritative count.
export default function RehearsalScrubTimeline({
  min,
  max,
  count,
  mappedRange = null,
  loopRange = null,
  onSeek,
  label = 'Rehearsal position',
}) {
  const trackRef = useRef(null);
  const bounds = { min: Math.min(min, max), max: Math.max(min, max) };
  const safeCount = clamp(Number(count) || bounds.min, bounds.min, bounds.max);
  const span = Math.max(1, bounds.max - bounds.min);
  const ratio = (safeCount - bounds.min) / span;

  const seekToClientX = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track || typeof onSeek !== 'function') return;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const fraction = clamp((clientX - rect.left) / rect.width, 0, 1);
    onSeek(Math.round(bounds.min + fraction * span));
  }, [bounds.min, onSeek, span]);

  const pointerDown = (event) => {
    event.preventDefault();
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch (_) { /* continue without capture */ }
    seekToClientX(event.clientX);
  };
  const pointerMove = (event) => {
    if (event.buttons !== 1 && event.pointerType !== 'touch') return;
    seekToClientX(event.clientX);
  };

  const keyDown = (event) => {
    if (typeof onSeek !== 'function') return;
    // MCRO001-F0037/T-MCRO-037 case H: this control and the Rehearsal panel both bind
    // Home/End (the panel's jump to the loop's start/end, per T-MCRO-036 case D). When
    // the slider itself holds focus its own bounds must win, so a handled key never
    // bubbles on to be silently overwritten by the panel handler.
    if (['ArrowRight', 'ArrowUp', 'ArrowLeft', 'ArrowDown', 'Home', 'End'].includes(event.key)) {
      event.stopPropagation();
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      onSeek(clamp(safeCount + 1, bounds.min, bounds.max));
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      onSeek(clamp(safeCount - 1, bounds.min, bounds.max));
    } else if (event.key === 'Home') {
      event.preventDefault();
      onSeek(bounds.min);
    } else if (event.key === 'End') {
      event.preventDefault();
      onSeek(bounds.max);
    }
  };

  const rangeStyle = (range) => {
    if (!range) return null;
    const start = clamp(Number(range.startCount), bounds.min, bounds.max);
    const end = clamp(Number(range.endCount), bounds.min, bounds.max);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return null;
    return {
      left: `${((start - bounds.min) / span) * 100}%`,
      width: `${Math.max(0, (end - start) / span) * 100}%`,
    };
  };
  const mappedStyle = rangeStyle(mappedRange);
  const loopStyle = !mappedRange ? rangeStyle(loopRange) : null;

  return <div className="mc-scrub-timeline" ref={trackRef} onPointerDown={pointerDown} onPointerMove={pointerMove}>
    {loopStyle && <span className="mc-scrub-loop-range" style={loopStyle} />}
    {mappedStyle && <span className="mc-scrub-mapped-range" style={mappedStyle} />}
    <button
      type="button"
      className="mc-scrub-thumb"
      role="slider"
      aria-label={label}
      aria-valuemin={bounds.min}
      aria-valuemax={bounds.max}
      aria-valuenow={safeCount}
      aria-valuetext={`Count ${safeCount}`}
      style={{ left: `${ratio * 100}%` }}
      onKeyDown={keyDown}
      onPointerDown={pointerDown}
    />
  </div>;
}
